/**
 * MODULE: _shared/frontend-shell/src/components/assistant/assistantContextUtils.ts
 * PURPOSE: Helpers for assistant context panel badge counts and visibility.
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 95 / 150 lines
 * RECENT: 2026-05-26 [2.2.1230] — shouldPeekMobileContextDrawerForLearningHint for mobile learning nudge.
 * RECENT_PRIOR: 2026-05-26 [2.2.1229] — formatLearningPipelineCatchUpHint for post-chat pipeline catch-up.
 * RECENT_PRIOR: 2026-05-25 [2.2.1224] — reasoning citation collectors for inline chat sources.
 */

import type {
  AssistantContextInsight,
  AssistantContextResponse,
  AssistantInsightSupportingEvent,
} from '@shared/lib/frontend/api/assistantContextApi';
import { hasEmailTemplateWelcomeHints } from './assistantWelcomePrompts';

export type AssistantReasoningCitation = {
  id: string;
  title: string;
  source?: string;
  insightTitle?: string;
  eventType?: string;
};

const EXPLAINABILITY_QUERY_RE =
  /\b(why|explain|what\s+(?:signals|happened|changed|drove|triggered)|how\s+do\s+you\s+know|reason|at\s+risk|stalled|what\s+evidence)\b/i;

export function userAsksForExplainability(userMessage: string): boolean {
  const text = (userMessage || '').trim();
  return text.length >= 4 && EXPLAINABILITY_QUERY_RE.test(text);
}

function citationFromEvent(
  ev: AssistantInsightSupportingEvent,
  insightTitle?: string,
): AssistantReasoningCitation | null {
  const title = (ev.title || ev.event_type || 'Activity').trim();
  if (!title) return null;
  const id = (ev.id || `${ev.source || 'src'}:${title}`).trim();
  return {
    id,
    title,
    source: ev.source?.trim() || undefined,
    insightTitle: insightTitle?.trim() || undefined,
    eventType: ev.event_type?.trim() || undefined,
  };
}

/** Prefer API `reasoning_citations`; fall back to flattening insight supporting_events. */
export function collectReasoningCitations(
  context: AssistantContextResponse | null,
  maxItems = 6,
): AssistantReasoningCitation[] {
  if (!context) return [];

  const apiRows = context.reasoning_citations ?? [];
  if (apiRows.length > 0) {
    return apiRows
      .slice(0, maxItems)
      .map((row) => ({
        id: row.id || `${row.source || 'src'}:${row.title || 'activity'}`,
        title: row.title || 'Activity',
        source: row.source,
        insightTitle: row.insight_title,
        eventType: row.event_type,
      }))
      .filter((row) => row.title);
  }

  const out: AssistantReasoningCitation[] = [];
  const seen = new Set<string>();
  for (const insight of context.top_insights ?? []) {
    appendInsightEvents(out, seen, insight, maxItems);
    if (out.length >= maxItems) break;
  }
  return out.slice(0, maxItems);
}

function appendInsightEvents(
  out: AssistantReasoningCitation[],
  seen: Set<string>,
  insight: AssistantContextInsight,
  maxItems: number,
) {
  const insightTitle = (insight.title || '').trim();
  for (const ev of insight.supporting_events ?? []) {
    const row = citationFromEvent(ev, insightTitle);
    if (!row || seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
    if (out.length >= maxItems) return;
  }
}

export function shouldShowInlineReasoningCitations(
  context: AssistantContextResponse | null,
  userMessage: string,
): boolean {
  const citations = collectReasoningCitations(context);
  if (!citations.length) return false;
  if (userAsksForExplainability(userMessage)) return true;
  return (context?.top_insights?.length ?? 0) > 0;
}

export function countAssistantContextItems(context: AssistantContextResponse | null): number {
  if (!context) return 0;
  let total = 0;
  if (context.focus_entity) total += 1;
  total += context.related_entities?.length ?? 0;
  total += context.top_insights?.length ?? 0;
  total += context.suggested_next_actions?.length ?? 0;
  if (context.tool_route?.execution_mode) total += 1;
  total += context.risk_alerts?.length ?? 0;
  total += context.learned_preferences?.length ?? 0;
  const eligible = context.promotion_pipeline?.eligible_candidates ?? 0;
  const almostReady = context.promotion_pipeline?.almost_ready_candidates ?? 0;
  if (eligible > 0 || almostReady > 0) total += 1;
  if (context.session_delta?.has_changes) {
    total += context.session_delta.changes?.length ?? 0;
  }
  if ((context.proactive_briefing ?? '').trim()) total += 1;
  if ((context.explainability_context ?? '').trim()) total += 1;
  return total;
}

export function hasAssistantContextContent(context: AssistantContextResponse | null): boolean {
  return countAssistantContextItems(context) > 0;
}

/** True when promotion pipeline has patterns ready or almost ready to promote. */
export function hasLearningPipelineAttention(context: AssistantContextResponse | null): boolean {
  if (!context) return false;
  const eligible = context.promotion_pipeline?.eligible_candidates ?? 0;
  const almostReady = context.promotion_pipeline?.almost_ready_candidates ?? 0;
  return eligible > 0 || almostReady > 0;
}

export type LearningPipelineSnapshot = {
  pending_candidates: number;
  almost_ready_candidates: number;
  eligible_candidates: number;
  active_memories: number;
};

export function snapshotLearningPipeline(
  context: AssistantContextResponse | null,
): LearningPipelineSnapshot | null {
  const pipe = context?.promotion_pipeline;
  if (!pipe) return null;
  return {
    pending_candidates: pipe.pending_candidates ?? 0,
    almost_ready_candidates: pipe.almost_ready_candidates ?? 0,
    eligible_candidates: pipe.eligible_candidates ?? 0,
    active_memories: pipe.active_memories ?? 0,
  };
}

/** User-facing hint when catch-up refresh detects self-learning pipeline progress. */
export function formatLearningPipelineCatchUpHint(
  before: LearningPipelineSnapshot | null,
  after: LearningPipelineSnapshot | null,
): string | null {
  if (!after) return null;
  const prev = before ?? {
    pending_candidates: 0,
    almost_ready_candidates: 0,
    eligible_candidates: 0,
    active_memories: 0,
  };

  const promoted = after.active_memories - prev.active_memories;
  const newEligible = after.eligible_candidates - prev.eligible_candidates;
  const newAlmost = after.almost_ready_candidates - prev.almost_ready_candidates;
  const newPending = after.pending_candidates - prev.pending_candidates;

  if (promoted > 0) {
    return promoted === 1
      ? 'A new learned preference is now active.'
      : `${promoted} new learned preferences are now active.`;
  }
  if (newEligible > 0) {
    return newEligible === 1
      ? 'A pattern is ready to promote into learned preferences.'
      : `${newEligible} patterns are ready to promote.`;
  }
  if (newAlmost > 0) {
    return newAlmost === 1
      ? 'A pattern moved closer to becoming a learned preference.'
      : `${newAlmost} patterns moved closer to learned preferences.`;
  }
  if (newPending > 0) {
    return newPending === 1
      ? 'Brazn started watching a new usage pattern.'
      : `Brazn started watching ${newPending} new patterns.`;
  }
  return null;
}

/** Count newly promoted learned preferences between catch-up snapshots. */
export function countNewActiveMemories(
  before: LearningPipelineSnapshot | null,
  after: LearningPipelineSnapshot | null,
): number {
  if (!after) return 0;
  const prev = before?.active_memories ?? 0;
  return Math.max(0, after.active_memories - prev);
}

/** Open mobile context drawer once per new learning catch-up hint [2.2.1230]. */
export function shouldPeekMobileContextDrawerForLearningHint(
  hint: string | null,
  previousHint: string | null,
  isMobileViewport: boolean,
): boolean {
  if (!hint || !isMobileViewport) return false;
  return hint !== previousHint;
}

/** True when empty-chat proactive advisor or learning pipeline should draw attention. */
export function hasProactiveContextAttention(context: AssistantContextResponse | null): boolean {
  if (!context) return false;
  if (hasEmailTemplateWelcomeHints(context)) return true;
  if ((context.risk_alerts?.length ?? 0) > 0) return true;
  if (context.session_delta?.has_changes && (context.session_delta.changes?.length ?? 0) > 0) {
    return true;
  }
  if ((context.learned_preferences?.length ?? 0) > 0) return true;
  return hasLearningPipelineAttention(context);
}
